import { api } from './client';
import type { Page } from './catalogs';

/** Податковий розрахунок J0500110 (об'єднана звітність ПДФО/ВЗ/ЄСВ). */

export type TaxReportType = 'REPORTING' | 'REPORTING_NEW' | 'CORRECTING';
export type TaxPeriodKind = 'MONTH' | 'QUARTER';

export interface TaxReport {
  submission_status?: string | null;
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  report_type: TaxReportType;
  period_kind: TaxPeriodKind;
  period_year: number;
  period_month: number | null;
  period_quarter: number | null;
  report_ordinal: number;
  appendices: string[];
  kved: string | null;
  prof_risk_class: number | null;
  sti_name: string | null;
  // ч.806: розрахунок «за відокремлений підрозділ» (лише 4ДФ).
  activity_place_id: string | null;
  activity_place_name: string | null;
  // ч.1295: коригуючий — який поданий розрахунок виправляє; чи є знімок пакета на підпис.
  corrects_report_id?: string | null;
  has_sent_package?: boolean;
  posted: boolean;
  deletion_mark: boolean;
  created_at: string;
}

export interface TaxReportCreate {
  organization_id: string;
  report_type?: TaxReportType;
  period_kind?: TaxPeriodKind;
  period_year: number;
  period_month?: number | null;
  period_quarter?: number | null;
  appendices?: string[];
  manual_only?: boolean;
  activity_place_id?: string | null;  // ч.806
  corrects_report_id?: string | null;  // ч.1295
}

/** ч.1295: підбір рядків виправлень з поданого розрахунку. */
export type CorrectionMode = 'exclude' | 'include' | 'replace' | 'esv_add' | 'esv_reduce';
export interface CorrectionSource {
  section: string;
  table: 'main' | 'grants';
  from_snapshot: boolean;
  columns: { tag: string; label: string }[];
  rows: { row_num: number; cells: Record<string, string | null> }[];
  source: {
    id: string; report_ordinal: number; report_type: TaxReportType;
    period_year: number; period_month: number | null; period_quarter: number | null;
    document_date: string;
  };
}

export interface TaxReportPersonRow {
  last_name: string;
  first_name: string;
  middle_name: string | null;
  inn: string;
  income_sign: string;
  gross: number;
  esv: number;
  pdfo: number;
  wz: number;
  days: number;
}

export interface TaxReportPreview {
  org: { name: string; edrpou: string | null };
  totals: {
    gross: number; esv_base: number; esv: number;
    pdfo: number; wz: number; insured_count: number;
  };
  persons: TaxReportPersonRow[];
}

/** Form-view: форма-бланк (рядки з офіційними назвами + правки). */
export interface TaxFormScalar {
  code: string;
  num: string | null;
  label: string;
  value: string | null;
  auto: string | null;
  is_manual: boolean;
  editable: boolean;
  kind: 'text' | 'date';
}
export interface TaxFormCell {
  value: string | null;
  auto: string | null;
  is_manual: boolean;
}
export interface TaxFormColumn {
  tag: string;
  num: string | null;
  label: string;
  kind: 'text' | 'date';
}
export interface TaxFormTable {
  key?: string;
  columns: TaxFormColumn[];
  rows: { row_num: number; cells: Record<string, TaxFormCell> }[];
}
export interface TaxFormView {
  code: string;
  title: string;
  scalars: TaxFormScalar[];
  table: TaxFormTable | null;
  tables?: TaxFormTable[];
}

export interface TaxCheckItem {
  form: string;
  code: string;
  message: string;
}
export interface TaxReportCheck {
  errors: TaxCheckItem[];
  warnings: TaxCheckItem[];
}

export const taxReportJ05 = {
  list: (params?: { organization_id?: string; period_year?: number; period_month?: number; period_from?: string; period_to?: string; sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number }) =>
    api.get<Page<TaxReport>>('/tax-report-j05', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<TaxReport>(`/tax-report-j05/${id}`).then((r) => r.data),
  create: (p: TaxReportCreate) =>
    api.post<TaxReport>('/tax-report-j05', p).then((r) => r.data),
  patch: (id: string, p: { document_date?: string }) =>
    api.patch<TaxReport>(`/tax-report-j05/${id}`, p).then((r) => r.data),
  preview: (id: string) =>
    api.get<TaxReportPreview>(`/tax-report-j05/${id}/preview`).then((r) => r.data),
  setLine: (id: string, line: {
    section: string; row_code: string; row_num?: number | null;
    value_text?: string | null;
  }) => api.post(`/tax-report-j05/${id}/lines`, line).then((r) => r.data),
  form: (id: string) =>
    api.get<{ forms: TaxFormView[] }>(`/tax-report-j05/${id}/form`).then((r) => r.data),
  check: (id: string) =>
    api.get<TaxReportCheck>(`/tax-report-j05/${id}/check`).then((r) => r.data),
  // «Зберегти» — чернетка розрахунку з'являється у Кабінеті ДПС (ідемпотентно).
  saveToCabinet: (id: string) =>
    api.post(`/tax-report-j05/${id}/save-to-cabinet`).then(() => undefined),
  files: (id: string) =>
    api.get<{ files: { fname: string; xml_base64: string }[] }>(`/tax-report-j05/${id}/files`).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/tax-report-j05/${id}`).then(() => undefined),
  // ч.1295: рядки поданого розрахунку і додавання виправлень у коригуючий.
  correctionSource: (id: string, params: { source_report_id: string; section: string; table?: 'main' | 'grants' }) =>
    api.get<CorrectionSource>(`/tax-report-j05/${id}/correction-source`, { params }).then((r) => r.data),
  addCorrectionRows: (id: string, payload: {
    section: string; table?: 'main' | 'grants';
    rows: { mode: CorrectionMode; cells: Record<string, string | null> }[];
  }) => api.post<{ added: number[] }>(`/tax-report-j05/${id}/correction-rows`, payload).then((r) => r.data),
};
